import { ChangeEvent, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import { useRegistrations, useEvents, exportRegistrations } from '@/services/events';
import RegistrationModal from './components/registration-modal';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { EVENT_REGISTRATION_STATUSES, EventRegistrationStatus, EventPaymentStatus, MessengerApp } from '@/models/event-registration';
import { formatDate } from '@/utils/helpers';

const STATUS_LABELS: Record<EventRegistrationStatus, string> = {
    confirmed: 'Confirmed',
    waitlisted: 'Waitlisted',
    cancelled: 'Cancelled',
};

const PAYMENT_STATUS_LABELS: Record<EventPaymentStatus, string> = {
    pending: 'Pending',
    verified: 'Verified',
    rejected: 'Rejected',
};

const MESSENGER_LABELS: Record<MessengerApp, string> = {
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    both: 'WhatsApp/Telegram',
};

const EventRegistrations = () => {
    const [page, setPage] = useState(1); // 1-indexed for the API
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [status, setStatus] = useState<EventRegistrationStatus | ''>('');
    const [eventId, setEventId] = useState('');

    const { data: eventsRes } = useEvents();
    const events = eventsRes?.data ?? [];

    const { data: result, isLoading } = useRegistrations({
        page,
        limit: rowsPerPage,
        status: status || undefined,
        eventId: eventId || undefined,
    });
    const registrations = result?.data?.registrations ?? [];
    const total = result?.data?.total ?? 0;

    const [isOpen, setIsOpen] = useState(false);
    const [selectedRegistrationId, setSelectedRegistrationId] = useState<string>();

    const toggle = () => setIsOpen((prev) => !prev);
    const handleClick = (id: string) => {
        setSelectedRegistrationId(id);
        toggle();
    };

    const handleChangePage = (_: unknown, item: number) => setPage(item + 1);
    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(1);
    };

    const [exporting, setExporting] = useState(false);
    const handleExport = async () => {
        setExporting(true);
        try {
            // Exports ALL rows for the active event/status filter (not just the visible page).
            await exportRegistrations({ status: status || undefined, eventId: eventId || undefined });
        } catch (e) {
            console.error('Failed to export registrations:', e);
            alert('Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Event Registrations</h3>
                    <h3 className="page-subhead subhead">Everyone who registered for an in-person meditation event.</h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            <Row className="align-items-center">
                                <Col xs={12} lg={6}>
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Event"
                                        value={eventId}
                                        onChange={(e) => {
                                            setEventId(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <MaterialFormOption value="">All events</MaterialFormOption>
                                        {events.map((ev) => (
                                            <MaterialFormOption key={ev.id} value={ev.id}>
                                                {ev.title}
                                            </MaterialFormOption>
                                        ))}
                                    </MaterialTextField>
                                </Col>
                                <Col xs={12} lg={4}>
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Status"
                                        value={status}
                                        onChange={(e) => {
                                            setStatus(e.target.value as EventRegistrationStatus | '');
                                            setPage(1);
                                        }}
                                    >
                                        <MaterialFormOption value="">All statuses</MaterialFormOption>
                                        {EVENT_REGISTRATION_STATUSES.map((s) => (
                                            <MaterialFormOption key={s} value={s}>
                                                {STATUS_LABELS[s]}
                                            </MaterialFormOption>
                                        ))}
                                    </MaterialTextField>
                                </Col>
                                <Col xs={12} lg={2} className="d-flex justify-content-lg-end mb-2">
                                    <button
                                        type="button"
                                        onClick={handleExport}
                                        disabled={exporting}
                                        className="btn btn-outline-primary btn-sm mb-0"
                                        title="Download all rows for the current filter as Excel"
                                        style={{ whiteSpace: 'nowrap' }}
                                    >
                                        {exporting ? 'Exporting…' : 'Export Excel'}
                                    </button>
                                </Col>
                            </Row>

                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead />
                                            <TableBody>
                                                {registrations.map((d) => (
                                                    <TableRow role="checkbox" onClick={() => handleClick(d.id)} tabIndex={-1} key={d.id}>
                                                        <TableCell component="th" scope="row" padding="none">
                                                            {d.fullName}
                                                        </TableCell>
                                                        <TableCell>{d.email}</TableCell>
                                                        <TableCell>
                                                            {d.phone}{' '}
                                                            <span style={{ color: '#999' }}>
                                                                ({MESSENGER_LABELS[d.messengerApp] ?? d.messengerApp})
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>{d.eventTitle}</TableCell>
                                                        <TableCell>{STATUS_LABELS[d.status] ?? d.status}</TableCell>
                                                        <TableCell>
                                                            {(d.amountDue ?? 0) > 0
                                                                ? (PAYMENT_STATUS_LABELS[d.paymentStatus] ?? d.paymentStatus)
                                                                : 'Free'}
                                                        </TableCell>
                                                        <TableCell>{formatDate(d.created_at)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {registrations.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={7} className="text-center">
                                                            No registrations found.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={total}
                                        rowsPerPage={rowsPerPage}
                                        page={page - 1}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[20, 50, 100]}
                                        dir="ltr"
                                        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <RegistrationModal isOpen={isOpen} toggle={toggle} selectedRegistrationId={selectedRegistrationId} />
        </Container>
    );
};

export default EventRegistrations;
